import {
    MAXIMAL_YEAR,
    MINIMAL_YEAR,
} from '@/components/TimelineIntervalChanger/composables/useTimelineIntervalChanger';
import { getDateTimeFromYearAndQuarter } from '@/navigation/utils';
import { DateTime, DurationLike } from 'luxon';
import { computed, Ref, ref, toValue } from 'vue';

export function sortByDateAsc(a: string, b: string) {
    const startA = DateTime.fromISO(a);
    const startB = DateTime.fromISO(b);
    return startA < startB ? -1 : startA > startB ? 1 : 0;
}

export function useTimelineRange(quarter: Ref<string>, year: Ref<string>) {
    // DateTime selected in the TimelineIntervalChanger
    // Timeline allows to expand the displayed time range by quarters
    const monthsBefore = ref(0); // Quarters before selected start DateTime
    const monthsAfter = ref(0); // Quarters after selected end DateTime

    const selectedStartDateTime: Ref<undefined | DateTime> = ref(undefined);

    function onExpandToPreviousMonth() {
        monthsBefore.value = toValue(monthsBefore) + 1;
    }

    function onExpandToNextMonth() {
        monthsAfter.value = toValue(monthsAfter) + 1;
    }

    // DateTimes the Timeline actually shows
    const displayedStartDateTime = computed(() => {
        if (selectedStartDateTime.value) {
            return selectedStartDateTime.value.startOf('quarter').startOf('day').minus({ months: monthsBefore.value });
        }
    });
    const displayedEndDateTime = computed(() => {
        if (selectedStartDateTime.value) {
            return selectedStartDateTime.value.startOf('quarter').plus({ months: toValue(monthsAfter) + 3 });
        }
    });

    const isTimeSpanModified = computed(() => {
        return monthsBefore.value !== 0 || toValue(monthsAfter) !== 0;
    });

    const previousMonthButton = computed(() => {
        if (displayedStartDateTime.value) {
            const isDisabled =
                displayedStartDateTime.value.year === MINIMAL_YEAR && displayedStartDateTime.value.month === 1;

            return isDisabled ? 'Disabled' : 'Visible';
        }
        return 'Disabled';
    });
    const nextMonthButton = computed(() => {
        if (displayedStartDateTime.value) {
            const isDisabled =
                displayedStartDateTime.value.year === MAXIMAL_YEAR && displayedStartDateTime.value.month === 12;

            return isDisabled ? 'Disabled' : 'Visible';
        }
        return 'Disabled';
    });

    const dateTimeFromRoute = computed(() => getDateTimeFromYearAndQuarter(year.value, quarter.value));

    function addToStartDateTime(duration: DurationLike) {
        if (selectedStartDateTime.value) {
            const newDateTime = selectedStartDateTime.value.plus(duration);
            selectedStartDateTime.value = newDateTime;
            monthsBefore.value = 0;
            monthsAfter.value = 0;
            quarter.value = newDateTime.quarter.toString();
            year.value = newDateTime.year.toString();
            return newDateTime;
        }
    }

    function subtractFromStartDateTime(duration: DurationLike) {
        if (selectedStartDateTime.value) {
            const newDateTime = selectedStartDateTime.value.minus(duration);
            selectedStartDateTime.value = newDateTime;
            monthsBefore.value = 0;
            monthsAfter.value = 0;
            quarter.value = newDateTime.quarter.toString();
            year.value = newDateTime.year.toString();
            return newDateTime;
        }
    }

    function setSelectedStartDateTime(value: DateTime) {
        selectedStartDateTime.value = value;
        quarter.value = value.quarter.toString();
        year.value = value.year.toString();
        return {
            newTimelineStart: value.startOf('quarter').startOf('day').minus({ months: monthsBefore.value }),
            newTimelineEnd: value.startOf('quarter').plus({ months: toValue(monthsAfter) + 3 }),
        };
    }

    return {
        selectedStartDateTime,
        startDateTime: displayedStartDateTime,
        endDateTime: displayedEndDateTime,
        isTimeSpanModified,
        previousMonthButton,
        nextMonthButton,
        dateTimeFromRoute,
        onExpandToPreviousMonth,
        onExpandToNextMonth,
        addToStartDateTime,
        subtractFromStartDateTime,
        setSelectedStartDateTime,
    };
}
